import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Eye, EyeOff, CheckCircle, XCircle, Loader2 } from "lucide-react";
import Header from "@/components/layout/Header";

const EmployeeSetup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [employee, setEmployee] = useState<any>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const token = searchParams.get("token");

  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setValidating(false);
      setLoading(false);
    }
  }, [token]);

  const validateToken = async () => {
    try {
      const { data: empData, error } = await supabase
        .from('employees')
        .select('*, companies(name)')
        .eq('invitation_token', token)
        .single();

      if (error || !empData) {
        console.error('Invalid token:', error);
        setTokenValid(false);
        setValidating(false);
        setLoading(false);
        return;
      }

      // Check if token expired
      if (empData.invitation_expires_at && new Date(empData.invitation_expires_at) < new Date()) {
        console.error('Token expired');
        setTokenValid(false);
        setValidating(false);
        setLoading(false);
        return;
      }

      // Check if already activated
      if (empData.profile_id || empData.invitation_status === 'actif') {
        toast({
          title: "Compte déjà activé",
          description: "Votre compte est déjà actif. Vous pouvez vous connecter."
        });
        navigate('/auth');
        return;
      }

      setEmployee(empData);
      setTokenValid(true);
    } catch (err) {
      console.error('Token validation error:', err);
      setTokenValid(false);
    } finally {
      setValidating(false);
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast({
        variant: "destructive",
        title: "Mot de passe trop court",
        description: "Le mot de passe doit contenir au moins 6 caractères"
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Mots de passe différents",
        description: "Les mots de passe ne correspondent pas"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Create auth user
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: employee.email,
        password,
        options: {
          data: {
            first_name: employee.first_name,
            last_name: employee.last_name
          }
        }
      });

      if (signUpError) throw signUpError;

      if (!authData.user) {
        throw new Error("Failed to create user account");
      }

      // Create profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .insert({
          user_id: authData.user.id,
          email: employee.email,
          first_name: employee.first_name || '',
          last_name: employee.last_name || '',
          role: 'employee',
          company_id: employee.company_id
        })
        .select('id')
        .single();

      if (profileError) {
        console.error('Profile creation error:', profileError);
        // Profile might be created by trigger, try to fetch it
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', authData.user.id)
          .single();
        
        if (!existingProfile) {
          throw new Error("Failed to create profile");
        }
      }

      const profileId = profileData?.id || (await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', authData.user.id)
        .single()).data?.id;

      // Update employee with profile link
      const { error: updateError } = await supabase
        .from('employees')
        .update({
          profile_id: profileId,
          invitation_status: 'actif',
          accepted_at: new Date().toISOString(),
          password_set_at: new Date().toISOString(),
          invitation_token: null,
          invitation_expires_at: null
        })
        .eq('id', employee.id);

      if (updateError) {
        console.error('Employee update error:', updateError);
      }

      // Add employee role
      await supabase.from('user_roles').upsert({
        user_id: authData.user.id,
        role: 'employee'
      }, { onConflict: 'user_id,role' });

      toast({
        title: "Compte créé avec succès !",
        description: "Vous pouvez maintenant vous connecter à votre espace employé"
      });

      // If email confirmation is required, show message
      if (authData.user && !authData.session) {
        toast({
          title: "Confirmation requise",
          description: "Veuillez vérifier votre email pour confirmer votre compte"
        });
      }

      navigate('/auth');
    } catch (error: any) {
      console.error('Setup error:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Une erreur est survenue lors de la création du compte"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Vérification du lien...</p>
        </div>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-16">
          <Card className="max-w-md mx-auto">
            <CardHeader className="text-center">
              <XCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
              <CardTitle>Lien invalide ou expiré</CardTitle>
              <CardDescription>
                Ce lien d'invitation n'est plus valide. Veuillez contacter votre administrateur RH pour recevoir une nouvelle invitation.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate('/auth')}
              >
                Aller à la page de connexion
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-16">
        <Card className="max-w-md mx-auto">
          <CardHeader className="text-center">
            <CheckCircle className="h-16 w-16 text-primary mx-auto mb-4" />
            <CardTitle>Bienvenue {employee?.first_name} !</CardTitle>
            <CardDescription>
              Créez votre mot de passe pour accéder à votre espace Renest chez <strong>{employee?.companies?.name}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input 
                  value={employee?.email || ''} 
                  disabled 
                  className="bg-muted"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 6 caractères"
                    required
                    minLength={6}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Répétez votre mot de passe"
                  required
                />
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Création en cours...
                  </>
                ) : (
                  "Créer mon compte"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmployeeSetup;
